import type { DocusignTemplateSignerResponse } from '@client/api/waterlily';
import type { MappedDocusignTab } from './docusign-tab-mapper';

export type DiffStatus = 'matched' | 'form-only' | 'template-only';

export interface DiffRow {
  label: string;
  tabType: string;
  status: DiffStatus;
  signer?: string;
  questionTitle?: string;
}

export interface DiffStatusConfig {
  label: string;
  rowClass: string;
  badgeVariant: 'success' | 'destructive' | 'secondary';
}

export const diffStatusConfig: Record<DiffStatus, DiffStatusConfig> = {
  matched: { label: 'Matched', rowClass: '', badgeVariant: 'success' },
  'template-only': { label: 'Template only', rowClass: 'bg-yellow-50', badgeVariant: 'destructive' },
  'form-only': { label: 'Form only', rowClass: 'bg-muted/50', badgeVariant: 'secondary' },
};

// Table column patterns use "-N" as a row-index placeholder.
// e.g. "beneficiaryDesignations-date-of-birth-N" matches "beneficiaryDesignations-date-of-birth-3"
const TABLE_ROW_SUFFIX = /-\d+$/;

export function buildDiffRows(
  formTabs: MappedDocusignTab[],
  signers: DocusignTemplateSignerResponse[],
): DiffRow[] {
  const formLabelSet = new Map<string, MappedDocusignTab>();
  const tableColumnPrefixes = new Map<string, MappedDocusignTab>();

  for (const tab of formTabs) {
    for (const label of tab.tabLabels) {
      if (label.endsWith('-N')) {
        tableColumnPrefixes.set(label.slice(0, -2), tab);
      } else {
        formLabelSet.set(label, tab);
      }
    }
  }

  function resolveFormTab(templateLabel: string): MappedDocusignTab | undefined {
    const exact = formLabelSet.get(templateLabel);
    if (exact) return exact;
    if (TABLE_ROW_SUFFIX.test(templateLabel)) {
      const prefix = templateLabel.replace(TABLE_ROW_SUFFIX, '');
      return tableColumnPrefixes.get(prefix);
    }
    return undefined;
  }

  const templateRows: { label: string; tabType: string; signer: string }[] = [];
  for (const signer of signers) {
    const signerName = signer.roleName;
    for (const t of signer.tabs.textTabs) {
      templateRows.push({ label: t.tabLabel, tabType: 'text', signer: signerName });
    }
    for (const t of signer.tabs.checkboxTabs) {
      templateRows.push({ label: t.tabLabel, tabType: 'checkbox', signer: signerName });
    }
    for (const t of signer.tabs.radioGroupTabs) {
      templateRows.push({ label: t.groupName, tabType: 'radio', signer: signerName });
    }
    for (const t of signer.tabs.fullNameTabs) {
      templateRows.push({ label: t.tabLabel, tabType: 'fullName', signer: signerName });
    }
    // signHereTabs, initialHereTabs, dateTabs are signature placement tabs — excluded from diff
  }

  const templateLabelSet = new Set(templateRows.map(r => r.label));
  const result: DiffRow[] = [];

  for (const row of templateRows) {
    const formTab = resolveFormTab(row.label);
    result.push({
      label: row.label,
      tabType: row.tabType,
      signer: row.signer,
      status: formTab ? 'matched' : 'template-only',
      questionTitle: formTab?.questionTitle,
    });
  }

  for (const [label, tab] of formLabelSet.entries()) {
    if (!templateLabelSet.has(label)) {
      result.push({ label, tabType: tab.tabType, status: 'form-only', questionTitle: tab.questionTitle });
    }
  }

  for (const [prefix, tab] of tableColumnPrefixes.entries()) {
    const hasAnyTemplateRow = templateRows.some(
      r => TABLE_ROW_SUFFIX.test(r.label) && r.label.replace(TABLE_ROW_SUFFIX, '') === prefix,
    );
    if (!hasAnyTemplateRow) {
      result.push({
        label: `${prefix}-N`,
        tabType: tab.tabType,
        status: 'form-only',
        questionTitle: tab.questionTitle,
      });
    }
  }

  return result;
}
